import React from "react";
import { CREST } from "@/constants/testIds";
import { useOnboardingForm } from "./onboarding/useOnboardingForm";
import {
  STEPS,
  WelcomeStep, ContactStep, SizingStep,
  StyleStep, CategoriesStep, BrandsStep, FinishStep,
} from "./onboarding/OnboardingSteps";

/**
 * Crest Profile onboarding wizard — a 7-step modal that runs the first time
 * a member signs in and never re-runs (`mark_onboarded: true` flips a flag on
 * the user record).
 *
 * Composition:
 * - `useOnboardingForm` owns state and side-effects (form, toggle, save, skip).
 * - `OnboardingSteps.jsx` exports the 7 step panes + STEPS labels.
 * - This file is just the shell: backdrop, header, progress bar, body switch,
 *   and footer with Back/Next/Skip/Save controls.
 */
export default function OnboardingWizard({ open, onClose, options }) {
  const w = useOnboardingForm({ open, onClose });

  if (!open) return null;

  const styles = options?.styles || [];
  const categories = options?.categories || [];
  const brands = options?.brands || [];

  return (
    <div
      data-testid={CREST.wizard}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-[#1A1A1A]/70"
    >
      <div className="relative w-full max-w-2xl bg-[#F9F8F4] border border-[#E5E3D8] shadow-2xl">
        <button
          type="button"
          onClick={() => onClose?.({ saved: false })}
          aria-label="Close"
          data-testid={CREST.wizardClose}
          className="absolute top-4 right-5 text-[#7A7A7A] hover:text-[#1A422D] text-2xl leading-none"
        >
          ×
        </button>

        <Header subtitle={w.headerSubtitle} step={w.step} />

        <div className="px-8 sm:px-12 py-8 max-h-[60vh] overflow-y-auto">
          {w.error && (
            <div className="mb-5 px-3 py-2 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">
              {w.error}
            </div>
          )}

          <StepBody
            step={w.step}
            user={w.user}
            form={w.form}
            setForm={w.setForm}
            toggle={w.toggle}
            styles={styles}
            categories={categories}
            brands={brands}
            brandOther={w.brandOther}
            setBrandOther={w.setBrandOther}
            addBrandOther={w.addBrandOther}
          />
        </div>

        <Footer
          step={w.step}
          busy={w.busy}
          onSkip={w.skip}
          onBack={() => w.setStep((s) => Math.max(0, s - 1))}
          onNext={() => w.setStep((s) => Math.min(STEPS.length - 1, s + 1))}
          onSave={w.save}
        />
      </div>
    </div>
  );
}

function Header({ subtitle, step }) {
  return (
    <div className="px-8 sm:px-12 pt-10 pb-4 border-b border-[#E5E3D8]">
      <div className="overline" style={{ color: "#B59A5A" }}>Welcome to the Crest Club</div>
      <h2 className="font-serif text-3xl sm:text-4xl mt-2 text-[#1A422D] leading-tight">
        Build Your Crest Profile
      </h2>
      <p className="text-sm text-[#4A4A4A] mt-3">{subtitle}</p>
      <div className="flex items-center gap-1.5 mt-5" data-testid={CREST.wizardStep}>
        {STEPS.map((stepName, i) => (
          <span
            key={stepName}
            data-testid={`${CREST.wizardStep}-${i}`}
            data-active={i <= step ? "true" : "false"}
            className={`h-0.5 flex-1 ${i <= step ? "bg-[#1A422D]" : "bg-[#E5E3D8]"}`}
          />
        ))}
      </div>
    </div>
  );
}

function StepBody({ step, user, form, setForm, toggle, styles, categories, brands, brandOther, setBrandOther, addBrandOther }) {
  switch (step) {
    case 0: return <WelcomeStep user={user} />;
    case 1: return <ContactStep form={form} setForm={setForm} />;
    case 2: return <SizingStep form={form} setForm={setForm} />;
    case 3: return <StyleStep form={form} toggle={toggle} styles={styles} />;
    case 4: return <CategoriesStep form={form} toggle={toggle} categories={categories} />;
    case 5: return (
      <BrandsStep
        form={form}
        toggle={toggle}
        brands={brands}
        brandOther={brandOther}
        setBrandOther={setBrandOther}
        addBrandOther={addBrandOther}
      />
    );
    case 6: return <FinishStep form={form} />;
    default: return null;
  }
}

function Footer({ step, busy, onSkip, onBack, onNext, onSave }) {
  const isLast = step >= STEPS.length - 1;
  return (
    <div className="px-8 sm:px-12 py-5 border-t border-[#E5E3D8] flex items-center justify-between gap-3">
      <button
        type="button"
        onClick={onSkip}
        disabled={busy}
        data-testid={CREST.wizardSkip}
        className="text-xs uppercase tracking-wider text-[#7A7A7A] hover:text-[#1A422D]"
      >
        Skip For Now
      </button>
      <div className="flex items-center gap-3">
        {step > 0 && (
          <button
            type="button"
            onClick={onBack}
            disabled={busy}
            data-testid={CREST.wizardBack}
            className="btn-secondary text-xs"
          >
            Back
          </button>
        )}
        {!isLast ? (
          <button
            type="button"
            onClick={onNext}
            disabled={busy}
            data-testid={CREST.wizardNext}
            className="btn-primary"
          >
            Continue
          </button>
        ) : (
          <button
            type="button"
            onClick={onSave}
            disabled={busy}
            data-testid={CREST.wizardSave}
            className="btn-primary"
          >
            {busy ? "Saving…" : "Save Crest Profile"}
          </button>
        )}
      </div>
    </div>
  );
}
